import { apiClient } from '../apiClient';

/**
 * Why a student surfaced in the queue — never a score.
 *
 * A composite "concern: 34" tells a trainer something is wrong and nothing
 * about what to do. Each reason maps to a different action, which is the whole
 * justification for having six instead of one number.
 */
export type AttentionReason =
  | 'stuck'
  | 'matches'
  | 'arrived_whole'
  | 'behind'
  | 'inactive'
  | 'never_started';

export interface AttentionRow {
  user_id: string;
  name: string;
  /** The reason to lead with — the most urgent one that applies. */
  reason: AttentionReason;
  /** A sentence actionable without opening anything else. */
  evidence: string;
  /** Set on handled rows: what the trainer did, and why. */
  handled?: AttentionHandling;
  /**
   * Every reason that applies. Filtering matches ANY of these, so a student
   * whose headline reason is "stuck" still appears under "inactive".
   */
  all_reasons: AttentionReason[];
  idle_days: number;
}

export interface AttentionThresholds {
  stuck_mins: number;
  stuck_runs: number;
  inactive_days: number;
  never_started_days: number;
  behind_gap_pct: number;
  behind_solved_per_week: number;
  arrived_whole_min: number;
  matches_min: number;
}

export interface AttentionQueue {
  batch_id: string;
  rows: AttentionRow[];
  /** True per-reason totals, before the per-reason cap trimmed the list. */
  counts: Partial<Record<AttentionReason, number>>;
  /** How many rows each reason had trimmed, so the UI can say "and 10 more". */
  overflow: Partial<Record<AttentionReason, number>>;
  total: number;
  flagged: number;
  clear: number;
  handled: number;
  thresholds: AttentionThresholds;
  /**
   * Rows the trainer has already dealt with, each carrying their note.
   *
   * The safeguard that makes read-only access safe. Without it an
   * administrator opening this sees a list of suspects; with it they see
   * mostly closed items and why each was closed, which turns the screen into
   * a record of what their trainer checked.
   */
  handled_rows: AttentionRow[];
  /** The viewer may look but not act. The API refuses writes regardless. */
  read_only: boolean;
}

/** A trainer's disposal of one reason. */
export interface AttentionHandling {
  reason: AttentionReason;
  note?: string;
  resolved_by: string;
  resolved_at: string;
}

/** One student in the full list, flagged or not. */
export interface RosterRow {
  user_id: string;
  name: string;
  coverage_pct: number;
  solved_last_7: number;
  idle_days: number;
  ever_active: boolean;
  /** Work worth asking about: arrived-whole solutions plus matched classmates. */
  to_ask: number;
  /** Absent for a student with nothing wrong, which is most of them. */
  reason?: AttentionReason;
}

/** One topic and how the class as a whole is coping with it. */
export interface TopicRow {
  topic_id: string;
  title: string;
  module_title: string;
  problems: number;
  /** Over (students x problems), so a few keen students cannot hide the rest. */
  completion_pct: number;
  students_started: number;
  match_pairs: number;
}

export interface SimilarityRunResult {
  problems_scanned: number;
  pairs_found: number;
  duration_ms: number;
}

export const attentionApi = {
  getRoster: (batchId: string) =>
    apiClient.get<RosterRow[]>(`/api/v1/attention/batches/${batchId}/roster`),

  getTopics: (batchId: string) =>
    apiClient.get<TopicRow[]>(`/api/v1/attention/batches/${batchId}/topics`),

  /**
   * Recompute cross-student code similarity for the batch.
   *
   * Triggered rather than scheduled: comparison is the expensive part of this
   * system, and a trainer pressing a button after a lab knows why they are
   * waiting a moment.
   */
  refreshSimilarity: (batchId: string) =>
    apiClient.post<SimilarityRunResult>(`/api/v1/attention/batches/${batchId}/similarity/refresh`),

  getQueue: (batchId: string) =>
    apiClient.get<AttentionQueue>(`/api/v1/attention/batches/${batchId}`),

  resolve: (batchId: string, studentId: string, reason: AttentionReason, note?: string) =>
    apiClient.post<{ status: string }>(
      `/api/v1/attention/batches/${batchId}/students/${studentId}/resolve`,
      { reason, note },
    ),

  unresolve: (batchId: string, studentId: string, reason: AttentionReason) =>
    apiClient.delete<{ status: string }>(
      `/api/v1/attention/batches/${batchId}/students/${studentId}/resolve`,
      { params: { reason } },
    ),
};
